/**
 * t520u: Channel settings › Posting › Permissions. Collapsed by default with a muted one-line summary
 * ("Everyone in the channel can post" until someone customises it). Presets for the common cases, then one row per
 * action: its default, or limited to selected roles, teams, buildings and people. Saved with the dialog's Save.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  CHANNEL_ACTION_INFO, CHANNEL_ALWAYS_NOTE, CHANNEL_PRESETS, applyChannelPreset, channelPermissionsSummary, channelPresetOf, channelPresetText,
  type ChannelAction, type ChannelActionInfo, type ChannelPreset, type ChannelPrincipal, type ChannelPrincipalType, type ChannelRules,
} from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { Select } from '@/components/ui';

export { sameChannelRules as sameRules, channelRulesForSave as rulesForSave } from '@tradeoutlet/shared';

type Option = { type: ChannelPrincipalType; id: string; name: string };

export function ChannelPermissionsSection({ convId, saved, draft, onDraft, disabled }: {
  convId: string; saved: ChannelRules | undefined; draft: ChannelRules | null; onDraft: (r: ChannelRules) => void; disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rules = draft ?? saved ?? {};
  const view = useQuery({ queryKey: ['channel-permissions', convId], queryFn: () => api.channelPermissions(convId) });
  const summary = channelPermissionsSummary(rules);
  return (
    <div className="border-t border-slate-200 pt-4 dark:border-slate-700" data-testid="channel-permissions">
      <button type="button" className="flex w-full items-center gap-2 text-left" aria-expanded={open} onClick={() => setOpen(o => !o)} data-testid="channel-permissions-toggle">
        {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
        <span className="text-sm font-medium">Permissions</span>
        {!open && <span className="ml-auto truncate text-xs text-slate-500" data-testid="channel-permissions-summary">{summary}</span>}
      </button>
      {open && <PermissionsBody rules={rules} onDraft={onDraft} disabled={disabled} roles={view.data?.roles ?? []} />}
    </div>
  );
}

function PermissionsBody({ rules, onDraft, disabled, roles }: { rules: ChannelRules; onDraft: (r: ChannelRules) => void; disabled: boolean; roles: { id: string; name: string }[] }) {
  const ws = useWorkspaceId() ?? '';
  const preset = channelPresetOf(rules);
  // Choices for "Add…": loaded only once the section is open and editable.
  const teams = useQuery({ queryKey: ['teams', ws], queryFn: () => api.teams(ws), enabled: !disabled && Boolean(ws) });
  const locations = useQuery({ queryKey: ['locations', ws], queryFn: () => api.locations(ws), enabled: !disabled && Boolean(ws) });
  const people = useQuery({ queryKey: ['members', ws, 'channel-permissions'], queryFn: () => api.members(ws), enabled: !disabled && Boolean(ws) });
  const options = useMemo<Option[]>(() => [
    ...roles.map(r => ({ type: 'role' as const, id: r.id, name: r.name })),
    ...(teams.data?.teams ?? []).map(t => ({ type: 'team' as const, id: t.id, name: t.name })),
    ...(locations.data?.locations ?? []).filter(l => !l.archived_at).map(l => ({ type: 'location' as const, id: l.id, name: l.name })),
    ...(people.data?.members ?? []).filter(m => m.status === 'active').map(m => ({ type: 'user' as const, id: m.user_id, name: m.display_name })),
  ], [roles, teams.data, locations.data, people.data]);
  const setAction = (a: ChannelAction, list: ChannelPrincipal[] | null) => onDraft({ ...rules, [a]: list });
  const pickPreset = (p: ChannelPreset) => onDraft(applyChannelPreset(rules, p, Array.isArray(rules.post) ? rules.post : []));

  return (
    <div className="mt-3 space-y-4">
      <div className="space-y-1.5">
        <div className="text-sm">Start from</div>
        <Select disabled={disabled} width="long" value={preset ?? 'custom'} onChange={e => e.target.value !== 'custom' && pickPreset(e.target.value as ChannelPreset)} data-testid="channel-permissions-preset" aria-label="Start from">
          {CHANNEL_PRESETS.map(p => <option key={p.key} value={p.key}>{channelPresetText(p)}</option>)}
          {!preset && <option value="custom">Custom</option>}
        </Select>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {CHANNEL_ACTION_INFO.map(info => (
          <ActionRow key={info.key} info={info} list={rules[info.key]} options={options} disabled={disabled} onChange={l => setAction(info.key, l)} />
        ))}
      </div>
      <p className="text-xs text-slate-500">{CHANNEL_ALWAYS_NOTE}</p>
    </div>
  );
}

function ActionRow({ info, list, options, disabled, onChange }: {
  info: ChannelActionInfo; list: ChannelPrincipal[] | null | undefined; options: Option[]; disabled: boolean; onChange: (l: ChannelPrincipal[] | null) => void;
}) {
  const limited = Array.isArray(list);
  const chosen = limited ? list : [];
  const has = (o: Option) => chosen.some(p => p.type === o.type && p.id === o.id);
  const group = (t: ChannelPrincipalType, label: string) => {
    const xs = options.filter(o => o.type === t && !has(o));
    return xs.length ? <optgroup label={label}>{xs.map(o => <option key={`${o.type}:${o.id}`} value={`${o.type}:${o.id}`}>{o.name}</option>)}</optgroup> : null;
  };
  const limitedLabel = info.moderation ? `${info.default_label} and selected people` : 'Only selected people';
  return (
    <div className="py-2.5" data-testid={`channel-permission-${info.key}`}>
      <div className="space-y-1.5">
        <div className="text-sm">{info.label}</div>
        <Select disabled={disabled} width="long" aria-label={info.label} value={limited ? 'limited' : 'default'} onChange={e => onChange(e.target.value === 'limited' ? [] : null)}>
          <option value="default">{info.default_label}</option>
          <option value="limited">{limitedLabel}</option>
        </Select>
      </div>
      {limited && (
        <div className="mt-2 space-y-1 pl-1">
          {!chosen.length && <p className="text-xs text-slate-500">{info.moderation ? 'Nobody else yet' : 'Nobody yet, apart from workspace owners and admins'}</p>}
          {chosen.map(p => (
            <div key={`${p.type}:${p.id}`} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate">{p.name ?? options.find(o => o.type === p.type && o.id === p.id)?.name ?? 'Unknown'} <span className="text-xs text-slate-500">{typeLabel(p.type)}</span></span>
              {!disabled && <button type="button" className="text-xs text-slate-500 hover:text-slate-800 hover:underline dark:hover:text-slate-200" onClick={() => onChange(chosen.filter(x => !(x.type === p.type && x.id === p.id)))}>Remove</button>}
            </div>
          ))}
          {!disabled && (
            <Select width="long" value="" aria-label={`Add to ${info.label}`} data-testid={`channel-permission-add-${info.key}`} onChange={e => {
              const [type, id] = e.target.value.split(':') as [ChannelPrincipalType, string];
              const o = options.find(x => x.type === type && x.id === id);
              if (o) onChange([...chosen, { type: o.type, id: o.id, name: o.name }]);
            }}>
              <option value="">Add…</option>
              {group('role', 'Roles')}{group('team', 'Departments & groups')}{group('location', 'Buildings')}{group('user', 'People')}
            </Select>
          )}
        </div>
      )}
    </div>
  );
}

const typeLabel = (t: ChannelPrincipalType) => (t === 'role' ? 'role' : t === 'team' ? 'team' : t === 'location' ? 'building' : '');
